import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API, fetchUsers as utilFetchUsers, fetchRoles as utilFetchRoles, fetchDepartments as utilFetchDepartments } from '../utils/api';
import axios from 'axios';
import RoleManagement from './RoleManagement';
import DepartmentManagement from './DepartmentManagement';
import ConfirmModal from './modals/ConfirmModal';
import SuccessModal from './modals/SuccessModal';
import ErrorModal from './modals/ErrorModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// SortIcon component moved outside to prevent re-creation on each render
const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) {
    return (
      <svg className="w-4 h-4 text-gray-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
        <path d="M5 12l5-5 5 5H5z"/>
      </svg>
    );
  }
  
  return sortDirection === 'asc' ? (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M5 12l5-5 5 5H5z"/>
    </svg>
  ) : (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M15 8l-5 5-5-5h10z"/>
    </svg>
  );
};

const UserManagement = ({ users, sites, fetchUsers }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [sortField, setSortField] = useState('full_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Archive feature state
  const [showArchived, setShowArchived] = useState(false);
  const [localUsers, setLocalUsers] = useState(users);
  const [checkingDelete, setCheckingDelete] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState({ show: false, user: null, clockRecordCount: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, user: null });
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Email validation state
  const [emailValidation, setEmailValidation] = useState({ isValidating: false, isValid: null, message: '' });
  const [editEmailValidation, setEditEmailValidation] = useState({ isValidating: false, isValid: null, message: '' });
  
  // Email change confirmation modal state
  const [emailChangeModal, setEmailChangeModal] = useState({
    isOpen: false,
    oldEmail: '',
    newEmail: '',
    userName: '',
    onConfirm: null
  });
  
  // Site unassignment warning modal state
  const [siteUnassignWarning, setSiteUnassignWarning] = useState({
    isOpen: false,
    siteId: null,
    siteName: '',
    userName: ''
  });
  
  const [newUser, setNewUser] = useState({
    first_name: '', middle_name: '', last_name: '', role: '', department: '', staff_id: '', assigned_sites: [],
    phone_number: '', email: '',
    date_of_birth: '', address: '', bank_name: '', account_number: '', sort_code: '', cis_registered: false, utr_number: '', ni_number: '', gross_hourly_rate: '', net_hourly_rate: ''
  });
  const [editUser, setEditUser] = useState({
    username: '',
    first_name: '', middle_name: '', last_name: '', role: '', department: '', staff_id: '', assigned_sites: [],
    phone_number: '', email: '',
    date_of_birth: '', address: '', bank_name: '', account_number: '', sort_code: '', cis_registered: false, utr_number: '', ni_number: '', gross_hourly_rate: '', net_hourly_rate: ''
  });
  const [newUserPhoto, setNewUserPhoto] = useState(null);
  const [editUserPhoto, setEditUserPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [selectedPhotoUser, setSelectedPhotoUser] = useState(null);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showDepartmentsModal, setShowDepartmentsModal] = useState(false);
  const newPhotoInputRef = React.useRef(null);
  const editPhotoInputRef = React.useRef(null);
  
  // Resend password email state
  const [isResendingPassword, setIsResendingPassword] = useState(false);
  
  // Subscription limit state
  const [teamMemberLimit, setTeamMemberLimit] = useState(null);
  const [currentMemberCount, setCurrentMemberCount] = useState(0);

  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'danger'
  });
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  // Calculate net hourly rate based on CIS registration status
  const calculateNetRate = (grossRate, isCISRegistered) => {
    const rate = parseFloat(grossRate);
    if (isNaN(rate)) return '';
    
    // If CIS registered: apply 20% deduction (net = 80% of gross)
    // If not CIS registered: no deduction (net = gross)
    return isCISRegistered ? (rate * 0.8).toFixed(2) : rate.toFixed(2);
  };

  // Fetch users with archive support
  const fetchUsersWithArchive = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?include_archived=${showArchived}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLocalUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Sync local users with props when showArchived changes
  useEffect(() => {
    fetchUsersWithArchive();
  }, [showArchived]);

  // Sync when props users change
  useEffect(() => {
    if (!showArchived) {
      setLocalUsers(users);
    }
  }, [users, showArchived]);

  // Check URL parameter for auto-opening the add modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openAddModal') === 'true') {
      setShowAddUserModal(true);
      // Clear the URL parameter
      navigate('/admin/users', { replace: true });
    }
  }, [location.search, navigate]);

  // Delete/Archive handlers
  const handleDeleteUser = async (user) => {
    // Check if user can be deleted before showing modal
    setCheckingDelete(true);
    try {
      const response = await axios.get(`${API}/admin/users/${user.id}/can-delete`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const { can_delete, must_archive, clock_record_count } = response.data;
      
      if (must_archive) {
        // User has clock records - show archive modal directly
        setArchiveConfirm({ 
          show: true, 
          user,
          clockRecordCount: clock_record_count 
        });
      } else if (can_delete) {
        // User can be deleted - show delete confirmation
        setDeleteConfirm({ show: true, user });
      } else {
        // User is already archived or other issue
        setErrorModal({
          isOpen: true,
          title: 'Cannot Delete',
          message: response.data.reason || 'Cannot delete this team member.'
        });
      }
    } catch (error) {
      console.error('Error checking delete eligibility:', error);
      // Fallback to showing delete modal - backend will validate again
      setDeleteConfirm({ show: true, user });
    } finally {
      setCheckingDelete(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.user) return;
    
    const userName = `${deleteConfirm.user.first_name} ${deleteConfirm.user.last_name}`.trim();
    setIsDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/users/${deleteConfirm.user.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setDeleteConfirm({ show: false, user: null });
      setIsDeleting(false);
      
      const totalDeleted = response.data.total_records_deleted || 0;
      setSuccessModal({ 
        isOpen: true, 
        title: 'Team Member Deleted',
        message: `"${userName}" has been permanently deleted.\n\n${totalDeleted} related record(s) removed.`
      });
      
      fetchUsersWithArchive();
      if (fetchUsers) fetchUsers();
      
    } catch (error) {
      console.error('Error deleting user:', error);
      setIsDeleting(false);
      setDeleteConfirm({ show: false, user: null });
      
      const errorDetail = error.response?.data?.detail || 'Failed to delete team member. Please try again.';
      setErrorModal({
        isOpen: true,
        title: 'Delete Failed',
        message: errorDetail
      });
    }
  };

  const handleArchiveUser = (user) => {
    setArchiveConfirm({ show: true, user, clockRecordCount: 0 });
  };

  const handleArchiveConfirm = async () => {
    if (!archiveConfirm.user) return;
    
    const userName = `${archiveConfirm.user.first_name} ${archiveConfirm.user.last_name}`.trim();
    setIsArchiving(true);
    try {
      await axios.post(`${API}/admin/users/${archiveConfirm.user.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setArchiveConfirm({ show: false, user: null, clockRecordCount: 0 });
      setIsArchiving(false);
      
      setSuccessModal({ 
        isOpen: true, 
        title: 'Team Member Archived',
        message: `"${userName}" has been archived.\n\nTheir historical data has been preserved. You can restore them later if needed.`
      });
      
      fetchUsersWithArchive();
      if (fetchUsers) fetchUsers();
      
    } catch (error) {
      console.error('Error archiving user:', error);
      setIsArchiving(false);
      setArchiveConfirm({ show: false, user: null, clockRecordCount: 0 });
      
      const errorDetail = error.response?.data?.detail || 'Failed to archive team member. Please try again.';
      setErrorModal({
        isOpen: true,
        title: 'Archive Failed',
        message: errorDetail
      });
    }
  };

  const handleUnarchiveUser = async (user) => {
    const userName = `${user.first_name} ${user.last_name}`.trim();
    try {
      await axios.post(`${API}/admin/users/${user.id}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSuccessModal({ 
        isOpen: true, 
        title: 'Team Member Restored',
        message: `"${userName}" has been restored.\n\nNote: Site assignments must be manually reassigned.`
      });
      
      fetchUsersWithArchive();
      if (fetchUsers) fetchUsers();
      
    } catch (error) {
      console.error('Error unarchiving user:', error);
      const errorDetail = error.response?.data?.detail || 'Failed to restore team member. Please try again.';
      setErrorModal({
        isOpen: true,
        title: 'Restore Failed',
        message: errorDetail
      });
    }
  };

  // Email validation function
  const validateEmail = async (email, isEdit = false, excludeUserId = null) => {
    const setValidation = isEdit ? setEditEmailValidation : setEmailValidation;
    
    if (!email || !email.trim()) {
      setValidation({ isValidating: false, isValid: false, message: 'Email is required' });
      return false;
    }
    
    // Basic format check
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(email)) {
      setValidation({ isValidating: false, isValid: false, message: 'Invalid email format' });
      return false;
    }
    
    setValidation({ isValidating: true, isValid: null, message: 'Checking...' });
    
    try {
      const response = await axios.post(`${API}/admin/validate-team-member-email`, {
        email: email.trim(),
        exclude_user_id: excludeUserId
      });
      
      if (response.data.available) {
        setValidation({ isValidating: false, isValid: true, message: 'Email is available' });
        return true;
      } else {
        setValidation({ isValidating: false, isValid: false, message: response.data.message });
        return false;
      }
    } catch (error) {
      setValidation({ isValidating: false, isValid: false, message: 'Error validating email' });
      return false;
    }
  };



  // Update net rate when gross rate changes for new user
  const handleNewUserGrossRateChange = (value) => {
    const netRate = calculateNetRate(value, newUser.cis_registered);
    setNewUser({
      ...newUser, 
      gross_hourly_rate: value, 
      net_hourly_rate: netRate
    });
  };

  // Update net rate when gross rate changes for edit user
  const handleEditUserGrossRateChange = (value) => {
    const netRate = calculateNetRate(value, editUser.cis_registered);
    setEditUser({
      ...editUser, 
      gross_hourly_rate: value, 
      net_hourly_rate: netRate
    });
  };

  // Update net rate when CIS registration status changes for new user
  const handleNewUserCISChange = (isChecked) => {
    const netRate = calculateNetRate(newUser.gross_hourly_rate, isChecked);
    setNewUser({
      ...newUser,
      cis_registered: isChecked,
      net_hourly_rate: netRate
    });
  };

  // Update net rate when CIS registration status changes for edit user
  const handleEditUserCISChange = (isChecked) => {
    const netRate = calculateNetRate(editUser.gross_hourly_rate, isChecked);
    setEditUser({
      ...editUser,
      cis_registered: isChecked,
      net_hourly_rate: netRate
    });
  };

  // Handle site checkbox toggle for edit user (with warning for unassignment)
  const handleEditSiteToggle = (siteId, siteName, isCurrentlyAssigned) => {
    if (isCurrentlyAssigned) {
      // Site is being unassigned - show warning
      const userName = `${editUser.first_name || editingUser?.first_name || ''} ${editUser.last_name || editingUser?.last_name || ''}`.trim();
      setSiteUnassignWarning({
        isOpen: true,
        siteId: siteId,
        siteName: siteName,
        userName: userName
      });
    } else {
      // Site is being assigned - no warning needed
      setEditUser({
        ...editUser,
        assigned_sites: [...editUser.assigned_sites, siteId]
      });
    }
  };

  // Confirm site unassignment
  const confirmSiteUnassignment = () => {
    const { siteId } = siteUnassignWarning;
    setEditUser({
      ...editUser,
      assigned_sites: editUser.assigned_sites.filter(id => id !== siteId)
    });
    setSiteUnassignWarning({ isOpen: false, siteId: null, siteName: '', userName: '' });
  };

  // Cancel site unassignment
  const cancelSiteUnassignment = () => {
    setSiteUnassignWarning({ isOpen: false, siteId: null, siteName: '', userName: '' });
  };

  useEffect(() => {
    fetchRolesAndDepartments();
  }, []);
  
  // Fetch subscription limits
  useEffect(() => {
    fetchSubscriptionLimits();
  }, [users]);

  const fetchRolesAndDepartments = async () => {
    try {
      const [rolesData, departmentsData] = await Promise.all([
        utilFetchRoles(),
        utilFetchDepartments()
      ]);
      setAvailableRoles(rolesData);
      setAvailableDepartments(departmentsData);
    } catch (error) {
      console.error('Failed to fetch roles and departments:', error);
    }
  };
  
  const fetchSubscriptionLimits = async () => {
    try {
      // Fetch organisation data (includes subscription_features)
      const orgResponse = await axios.get(`${API}/admin/organisation`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const org = orgResponse.data;
      const features = org.subscription_features || {};
      
      // Get max team members from features
      setTeamMemberLimit(features.maxTeamMembers || '0');
      
      // Count active users
      const activeUsers = users.filter(u => u.active !== false).length;
      setCurrentMemberCount(activeUsers);
      
    } catch (error) {
      console.error('Error fetching subscription limits:', error);
    }
  };
  
  // Helper to check if unlimited (case-insensitive)
  const isUnlimited = (limit) => {
    return limit && limit.toString().toLowerCase() === 'unlimited';
  };

  const validatePhotoFile = (file) => {
    if (!file.type.startsWith('image/')) {
      setErrorModal({
        isOpen: true,
        title: 'Invalid File Type',
        message: 'Please select an image file (JPG, PNG, etc.).'
      });
      return false;
    }
    
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setErrorModal({
        isOpen: true,
        title: 'File Too Large',
        message: 'File size must be less than 2MB.'
      });
      return false;
    }
    
    return true;
  };

  const handleNewUserPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file && validatePhotoFile(file)) {
      setNewUserPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleEditUserPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file && validatePhotoFile(file)) {
      setEditUserPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => setEditPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleEditPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file && validatePhotoFile(file)) {
      setEditUserPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => setEditPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const clearEditPhoto = () => {
    setEditUserPhoto(null);
    setEditPhotoPreview(null);
    if (editPhotoInputRef.current) {
      editPhotoInputRef.current.value = '';
    }
  };

  const uploadProfilePhoto = async (userId, photoFile) => {
    const formData = new FormData();
    formData.append('file', photoFile);
    
    const response = await axios.post(`${API}/admin/users/${userId}/profile-photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  };

  const deleteProfilePhoto = async (userId) => {
    const response = await axios.delete(`${API}/admin/users/${userId}/profile-photo`);
    return response.data;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedUsers = () => {
    const filteredUsers = localUsers.filter(user => 
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.department.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return filteredUsers.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle boolean values for active status
      if (sortField === 'active') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      } else {
        // Handle string values
        aValue = aValue || '';
        bValue = bValue || '';
        
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Count archived users for display
  const archivedCount = showArchived ? localUsers.filter(u => u.is_archived).length : 0;

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validate email before submission
    const isEmailValid = await validateEmail(newUser.email, false);
    if (!isEmailValid) {
      setErrorModal({
        isOpen: true,
        title: 'Invalid Email',
        message: emailValidation.message || 'Please enter a valid email address'
      });
      return;
    }
    
    try {
      // Don't send username - it will be auto-generated by backend
      const userData = { ...newUser };
      delete userData.username; // Remove username if present
      
      const response = await axios.post(`${API}/admin/users`, userData);
      const createdUser = response.data;
      
      let photoUploadFailed = false;
      // Upload profile photo if provided
      if (newUserPhoto) {
        try {
          await uploadProfilePhoto(createdUser.id, newUserPhoto);
        } catch (photoError) {
          console.error('Failed to upload profile photo:', photoError);
          photoUploadFailed = true;
        }
      }
      
      // Reset form
      setNewUser({ 
        first_name: '', middle_name: '', last_name: '', role: '', department: '', staff_id: '', assigned_sites: [],
        phone_number: '', email: '',
        date_of_birth: '', address: '', bank_name: '', account_number: '', sort_code: '', cis_registered: false, utr_number: '', ni_number: '', gross_hourly_rate: '', net_hourly_rate: ''
      });
      setNewUserPhoto(null);
      setPhotoPreview(null);
      setEmailValidation({ isValidating: false, isValid: null, message: '' });
      if (newPhotoInputRef.current) {
        newPhotoInputRef.current.value = '';
      }
      setShowAddUserModal(false);
      fetchUsers();
      
      const generatedUsername = createdUser.username || createdUser.generated_username;
      
      if (photoUploadFailed) {
        setErrorModal({
          isOpen: true,
          title: 'Partial Success',
          message: `User created with username "${generatedUsername}", but failed to upload profile photo. They will receive an email with a link to set their password.`
        });
      } else {
        setSuccessModal({
          isOpen: true,
          title: 'User Created',
          message: `User "${createdUser.first_name} ${createdUser.last_name}" has been successfully created with username "${generatedUsername}". They will receive an email with a link to set their password.`
        });
      }
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Creation Failed',
        message: 'Failed to create user: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/toggle-status`);
      fetchUsers();
      setSuccessModal({
        isOpen: true,
        title: 'Status Updated',
        message: response.data.message
      });
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Update Failed',
        message: 'Failed to toggle user status: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    
    // Recalculate net rate based on current CIS registration status
    const recalculatedNetRate = calculateNetRate(user.gross_hourly_rate || '', user.cis_registered || false);
    
    setEditUser({
      username: user.username || '',
      first_name: user.first_name || '',
      middle_name: user.middle_name || '',
      last_name: user.last_name || '',
      role: user.role,
      department: user.department,
      staff_id: user.staff_id,
      assigned_sites: user.assigned_sites || [],
      // Contact information
      phone_number: user.phone_number || '',
      email: user.email || '',
      // Personal details
      date_of_birth: user.date_of_birth || '',
      address: user.address || '',
      bank_name: user.bank_name || '',
      account_number: user.account_number || '',
      sort_code: user.sort_code || '',
      cis_registered: user.cis_registered || false,
      utr_number: user.utr_number || '',
      ni_number: user.ni_number || '',
      gross_hourly_rate: user.gross_hourly_rate || '',
      net_hourly_rate: recalculatedNetRate
    });
    setEditUserPhoto(null);
    setEditPhotoPreview(null);
    if (editPhotoInputRef.current) {
      editPhotoInputRef.current.value = '';
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    // Check if email has changed
    const emailHasChanged = editUser.email !== (editingUser.email || '') && editingUser.email;
    
    // Validate email if it has changed
    if (emailHasChanged) {
      const isEmailValid = await validateEmail(editUser.email, true, editingUser.id);
      if (!isEmailValid) {
        setErrorModal({
          isOpen: true,
          title: 'Invalid Email',
          message: editEmailValidation.message || 'Please enter a valid email address'
        });
        return;
      }
      
      // Show email change confirmation modal
      const userName = `${editUser.first_name || editingUser.first_name} ${editUser.last_name || editingUser.last_name}`.trim();
      setEmailChangeModal({
        isOpen: true,
        oldEmail: editingUser.email,
        newEmail: editUser.email,
        userName: userName,
        onConfirm: () => performUserUpdate(true)
      });
      return;
    }
    
    // No email change, proceed directly
    await performUserUpdate(false);
  };

  // Actual update function (called directly or after email change confirmation)
  const performUserUpdate = async (emailChanged) => {
    // Close the email change modal if it was open
    setEmailChangeModal({ isOpen: false, oldEmail: '', newEmail: '', userName: '', onConfirm: null });
    
    try {
      const updateData = {};
      
      // Username is read-only - not included in updates
      // Password is no longer handled here - users reset via email link
      
      // Basic info fields
      if (editUser.first_name !== (editingUser.first_name || '')) updateData.first_name = editUser.first_name;
      if (editUser.middle_name !== (editingUser.middle_name || '')) updateData.middle_name = editUser.middle_name;
      if (editUser.last_name !== (editingUser.last_name || '')) updateData.last_name = editUser.last_name;
      if (editUser.role !== editingUser.role) updateData.role = editUser.role;
      if (editUser.department !== editingUser.department) updateData.department = editUser.department;
      if (editUser.staff_id !== editingUser.staff_id) updateData.staff_id = editUser.staff_id;
      
      // Site assignments
      if (JSON.stringify(editUser.assigned_sites) !== JSON.stringify(editingUser.assigned_sites || [])) {
        updateData.assigned_sites = editUser.assigned_sites;
      }
      
      // Personal details
      if (editUser.date_of_birth !== (editingUser.date_of_birth || '')) updateData.date_of_birth = editUser.date_of_birth;
      if (editUser.address !== (editingUser.address || '')) updateData.address = editUser.address;
      
      // Contact information
      if (editUser.phone_number !== (editingUser.phone_number || '')) updateData.phone_number = editUser.phone_number;
      if (editUser.email !== (editingUser.email || '')) updateData.email = editUser.email;
      
      // Banking details
      if (editUser.bank_name !== (editingUser.bank_name || '')) updateData.bank_name = editUser.bank_name;
      if (editUser.account_number !== (editingUser.account_number || '')) updateData.account_number = editUser.account_number;
      if (editUser.sort_code !== (editingUser.sort_code || '')) updateData.sort_code = editUser.sort_code;
      if (editUser.cis_registered !== (editingUser.cis_registered || false)) updateData.cis_registered = editUser.cis_registered;
      if (editUser.utr_number !== (editingUser.utr_number || '')) updateData.utr_number = editUser.utr_number;
      if (editUser.ni_number !== (editingUser.ni_number || '')) updateData.ni_number = editUser.ni_number;
      if (editUser.gross_hourly_rate !== (editingUser.gross_hourly_rate || '')) updateData.gross_hourly_rate = editUser.gross_hourly_rate;
      if (editUser.net_hourly_rate !== (editingUser.net_hourly_rate || '')) updateData.net_hourly_rate = editUser.net_hourly_rate;
      
      // Work schedule fields - THIS WAS MISSING!

      // Only send update if there are changes
      if (Object.keys(updateData).length === 0) {
        setErrorModal({
          isOpen: true,
          title: 'No Changes',
          message: 'No changes detected.'
        });
        return;
      }

      await axios.put(`${API}/admin/users/${editingUser.id}`, updateData);
      
      let photoUploadFailed = false;
      // Upload new profile photo if provided
      if (editUserPhoto) {
        try {
          await uploadProfilePhoto(editingUser.id, editUserPhoto);
        } catch (photoError) {
          console.error('Failed to upload profile photo:', photoError);
          photoUploadFailed = true;
        }
      }
      
      const userName = `${editingUser.first_name} ${editingUser.last_name}`;
      
      setEditingUser(null);
      setEditUser({ 
        username: '',
        first_name: '', middle_name: '', last_name: '', role: '', department: '', staff_id: '', assigned_sites: [],
        phone_number: '', email: '',
        date_of_birth: '', address: '', bank_name: '', account_number: '', sort_code: '', cis_registered: false, utr_number: '', ni_number: '', gross_hourly_rate: '', net_hourly_rate: '',
        working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], start_time: '08:00', end_time: '17:00'
      });
      setEditUserPhoto(null);
      setEditPhotoPreview(null);
      setEditEmailValidation({ isValidating: false, isValid: null, message: '' });
      fetchUsers();
      
      if (photoUploadFailed) {
        setErrorModal({
          isOpen: true,
          title: 'Partial Success',
          message: 'User updated successfully, but failed to upload profile photo.'
        });
      } else if (emailChanged) {
        // Show special success message for email change
        setSuccessModal({
          isOpen: true,
          title: 'User Updated - Email Verification Required',
          message: `User "${userName}" has been updated. A verification email has been sent to the new email address. The user will not be able to log in until they verify their new email.`
        });
      } else {
        setSuccessModal({
          isOpen: true,
          title: 'User Updated',
          message: `User "${userName}" has been successfully updated.`
        });
      }
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Update Failed',
        message: 'Failed to update user: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const cancelAddUser = () => {
    setShowAddUserModal(false);
    setNewUser({ 
      first_name: '', middle_name: '', last_name: '', role: '', department: '', staff_id: '', assigned_sites: [],
      phone_number: '', email: '',
      date_of_birth: '', address: '', bank_name: '', account_number: '', sort_code: '', cis_registered: false, utr_number: '', ni_number: '', gross_hourly_rate: '', net_hourly_rate: ''
    });
    setEmailValidation({ isValidating: false, isValid: null, message: '' });
    setNewUserPhoto(null);
    setPhotoPreview(null);
    if (newPhotoInputRef.current) {
      newPhotoInputRef.current.value = '';
    }
  };

  const cancelEditUser = () => {
    setEditingUser(null);
    setEditUser({ 
      username: '',
      first_name: '', middle_name: '', last_name: '', role: '', department: '', staff_id: '', assigned_sites: [],
      phone_number: '', email: '',
      date_of_birth: '', address: '', bank_name: '', account_number: '', sort_code: '', cis_registered: false, utr_number: '', ni_number: '', gross_hourly_rate: '', net_hourly_rate: '',
      working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], start_time: '08:00', end_time: '17:00'
    });
    setEditUserPhoto(null);
    setEditPhotoPreview(null);
    setEditEmailValidation({ isValidating: false, isValid: null, message: '' });
  };

  // Handle resending password setup email
  const handleResendPasswordEmail = async (userId, userEmail, userName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Resend Password Setup Email',
      message: `Send a new password setup link to ${userEmail}?`,
      variant: 'info',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        setIsResendingPassword(true);
        try {
          const response = await axios.post(`${API}/admin/users/${userId}/resend-password-email`);
          if (response.data.success) {
            setSuccessModal({
              isOpen: true,
              title: 'Email Sent',
              message: response.data.message || `Password setup email has been sent to ${userEmail}`
            });
          } else {
            setErrorModal({
              isOpen: true,
              title: 'Failed to Send',
              message: response.data.message || 'Failed to send password setup email'
            });
          }
        } catch (error) {
          setErrorModal({
            isOpen: true,
            title: 'Error',
            message: error.response?.data?.detail || 'Failed to send password setup email'
          });
        } finally {
          setIsResendingPassword(false);
        }
      }
    });
  };


  const handleDeleteProfilePhoto = async (userId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Profile Photo',
      message: 'Are you sure you want to delete this profile photo?',
      variant: 'danger',
      onConfirm: async () => {
        try {
          setIsUploadingPhoto(true);
          await deleteProfilePhoto(userId);
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchUsers();
          setSuccessModal({
            isOpen: true,
            title: 'Photo Deleted',
            message: 'Profile photo deleted successfully!'
          });
        } catch (error) {
          setConfirmModal({ ...confirmModal, isOpen: false });
          setErrorModal({
            isOpen: true,
            title: 'Delete Failed',
            message: 'Failed to delete profile photo: ' + (error.response?.data?.detail || 'Unknown error')
          });
        } finally {
          setIsUploadingPhoto(false);
        }
      }
    });
  };

  return (
    <>
      {/* Dark Theme Wrapper */}
      <div className="min-h-screen bg-slate-900 -m-8 p-8">
      {/* Modern Header - Purple Gradient */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Team</h1>
            <p className="text-purple-200">Manage team members, roles, and organizational structure</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowRolesModal(true)}
              className="flex items-center px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors border border-white/20"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd"></path>
              </svg>
              Roles
            </button>
            <button
              onClick={() => setShowDepartmentsModal(true)}
              className="flex items-center px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors border border-white/20"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd"></path>
              </svg>
              Departments
            </button>
            {(() => {
              const limitReached = !isUnlimited(teamMemberLimit) && teamMemberLimit && currentMemberCount >= parseInt(teamMemberLimit);
              return (
                <button
                  onClick={() => setShowAddUserModal(!showAddUserModal)}
                  disabled={limitReached}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-medium ${
                    limitReached
                      ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                      : 'bg-purple-500 hover:bg-purple-400'
                  }`}
                  title={limitReached ? 'Team member limit reached. Upgrade your plan to add more members.' : 'Add a new team member'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>{limitReached ? 'Limit Reached' : 'Add Team Member'}</span>
                </button>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Subscription Info Note - Below Header */}
      {teamMemberLimit && (
        <div className={`mb-6 p-3 rounded-lg border ${
          isUnlimited(teamMemberLimit)
            ? 'bg-green-500/20 border-green-500/50'
            : currentMemberCount >= parseInt(teamMemberLimit)
            ? 'bg-red-500/20 border-red-500/50'
            : (currentMemberCount / parseInt(teamMemberLimit)) >= 0.5
            ? 'bg-yellow-500/20 border-yellow-500/50'
            : 'bg-blue-500/20 border-blue-500/50'
        }`}>
          <div className="flex items-start">
            <svg className={`w-5 h-5 mt-0.5 mr-2 ${
              isUnlimited(teamMemberLimit)
                ? 'text-green-400'
                : currentMemberCount >= parseInt(teamMemberLimit)
                ? 'text-red-400'
                : (currentMemberCount / parseInt(teamMemberLimit)) >= 0.5
                ? 'text-yellow-400'
                : 'text-blue-400'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className={`text-sm font-medium ${
                isUnlimited(teamMemberLimit)
                  ? 'text-green-300'
                  : currentMemberCount >= parseInt(teamMemberLimit)
                  ? 'text-red-300'
                  : (currentMemberCount / parseInt(teamMemberLimit)) >= 0.5
                  ? 'text-yellow-300'
                  : 'text-blue-300'
              }`}>
                {isUnlimited(teamMemberLimit) ? (
                  `You have ${currentMemberCount} active team member${currentMemberCount !== 1 ? 's' : ''}. Your current plan includes unlimited team members.`
                ) : currentMemberCount >= parseInt(teamMemberLimit) ? (
                  `You have ${currentMemberCount} active team member${currentMemberCount !== 1 ? 's' : ''}. Your current plan allows for a maximum of ${teamMemberLimit} active team member${parseInt(teamMemberLimit) !== 1 ? 's' : ''}. You need to deactivate one of your currently active team members to add a new one.`
                ) : (currentMemberCount / parseInt(teamMemberLimit)) >= 0.5 ? (
                  `You have ${currentMemberCount} active team member${currentMemberCount !== 1 ? 's' : ''}. Your current plan allows for a maximum of ${teamMemberLimit} active team member${parseInt(teamMemberLimit) !== 1 ? 's' : ''}. Once you reach this limit, you will need to deactivate one of your currently active team members to add a new one.`
                ) : (
                  `You have ${currentMemberCount} active team member${currentMemberCount !== 1 ? 's' : ''}. Your current plan allows for a maximum of ${teamMemberLimit} active team member${parseInt(teamMemberLimit) !== 1 ? 's' : ''}.`
                )}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 mb-8">
        {/* Table Header with Search */}
        <div className="px-6 py-4 border-b border-slate-700">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">All Team Members</h2>
            <div className="flex items-center gap-4">
              {/* Show Archived Toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="relative w-9 h-5 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                <span className="text-sm text-gray-400">
                  Show Archived
                  {showArchived && archivedCount > 0 && (
                    <span className="ml-1 text-amber-400">({archivedCount})</span>
                  )}
                </span>
              </label>
              
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <svg className="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-64 pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              {searchTerm && (
                <div className="text-sm text-gray-400">
                  {getSortedUsers().length} of {localUsers.length} users
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Add New User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-2xl w-full max-w-4xl mx-4 max-h-[95vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Add New Team Member</h3>
                    <p className="text-purple-200">Create a new user account</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddUserModal(false)}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-8 overflow-y-auto max-h-[calc(95vh-100px)]">
              <form onSubmit={handleCreateUser} className="space-y-8">
                
                {/* Profile Photo Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    Profile Photo
                  </h4>
                  <div className="flex items-center space-x-6">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                        {photoPreview ? (
                          <img src={photoPreview} alt="Profile preview" className="w-full h-full object-cover" />
                        ) : (
                          <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
                          </svg>
                        )}
                      </div>
                    </div>
                    <div>
                      <input
                        ref={newPhotoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleNewUserPhotoChange}
                        className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 transition-colors"
                      />
                      <p className="mt-1 text-xs text-gray-400">Max 2MB. JPG, PNG, or GIF. Optional field.</p>
                    </div>
                  </div>
                </div>

                {/* Personal & Login Information Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    Personal & Login Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                      <input
                        type="text"
                        value=""
                        placeholder="Will be auto-generated"
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg cursor-not-allowed text-gray-400 italic"
                        readOnly
                        title="Username will be automatically generated from first and last name"
                      />
                      <p className="mt-1 text-xs text-gray-400">Format: f.lastname (e.g., j.smith)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newUser.first_name}
                        onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newUser.last_name}
                        onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Middle Name</label>
                      <input
                        type="text"
                        value={newUser.middle_name}
                        onChange={(e) => setNewUser({...newUser, middle_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        onBlur={(e) => validateEmail(e.target.value, false)}
                        className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                          emailValidation.isValid === false ? 'border-red-500' : 
                          emailValidation.isValid === true ? 'border-green-500' : 'border-slate-600'
                        }`}
                        placeholder="Required for password setup"
                        required
                      />
                      {emailValidation.message && (
                        <p className={`mt-1 text-xs ${emailValidation.isValid ? 'text-green-400' : 'text-red-400'}`}>
                          {emailValidation.isValidating ? 'Checking...' : emailValidation.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Date of Birth</label>
                      <DatePicker
                        selected={newUser.date_of_birth ? new Date(newUser.date_of_birth) : null}
                        onChange={(date) => {
                          if (date) {
                            const formatted = date.toISOString().split('T')[0];
                            setNewUser({...newUser, date_of_birth: formatted});
                          } else {
                            setNewUser({...newUser, date_of_birth: ''});
                          }
                        }}
                        dateFormat="dd/MM/yyyy"
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        yearDropdownItemNumber={100}
                        scrollableYearDropdown
                        maxDate={new Date()}
                        minDate={new Date('1920-01-01')}
                        placeholderText="Select date of birth"
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        wrapperClassName="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={newUser.phone_number}
                        onChange={(e) => setNewUser({...newUser, phone_number: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        placeholder="e.g., +44 7XXX XXXXXX"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                      <input
                        type="text"
                        value={newUser.address}
                        onChange={(e) => setNewUser({...newUser, address: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                    </div>
                  </div>
                  {/* Password Setup Info Box */}
                  <div className="mt-4 p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h5 className="text-sm font-medium text-blue-300">Password Setup</h5>
                        <p className="text-sm text-blue-200/80 mt-1">
                          The team member will receive an email with a secure link to set their own password. The link is valid for 48 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Work Details Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6z" />
                      </svg>
                    </div>
                    Work Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newUser.role}
                        onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        required
                      >
                        <option value="">Select Role</option>
                        {availableRoles.map((role) => (
                          <option key={role.id} value={role.role_name}>
                            {role.role_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Department <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newUser.department}
                        onChange={(e) => setNewUser({...newUser, department: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        required
                      >
                        <option value="">Select Department</option>
                        {availableDepartments.map((dept) => (
                          <option key={dept.id} value={dept.department_name}>
                            {dept.department_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Staff ID</label>
                      <input
                        type="text"
                        value={newUser.staff_id}
                        onChange={(e) => setNewUser({...newUser, staff_id: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Assigned Sites</label>
                      <select
                        multiple
                        value={newUser.assigned_sites}
                        onChange={(e) => setNewUser({...newUser, assigned_sites: Array.from(e.target.selectedOptions, option => option.value)})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors min-h-[80px]"
                      >
                        {sites.map(site => (
                          <option key={site.id} value={site.id}>
                            {site.site_name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-sm text-gray-400">Hold Ctrl/Cmd to select multiple sites</p>
                    </div>
                  </div>
                </div>

                {/* Financial Information Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    Pay Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Gross Hourly Rate (£) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={newUser.gross_hourly_rate}
                        onChange={(e) => handleNewUserGrossRateChange(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Net Hourly Rate (£)</label>
                      <input
                        type="number"
                        value={newUser.net_hourly_rate}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-gray-400 cursor-not-allowed focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        step="0.01"
                        min="0"
                        readOnly
                      />
                      <p className="text-xs text-gray-400 mt-1">Automatically calculated as 80% of gross rate if CIS Registered</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">NI Number</label>
                      <input
                        type="text"
                        value={newUser.ni_number}
                        onChange={(e) => setNewUser({...newUser, ni_number: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        placeholder="Optional - Add if available"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newUser.cis_registered}
                          onChange={(e) => handleNewUserCISChange(e.target.checked)}
                          className="rounded border-slate-600 bg-slate-700 text-purple-600 focus:ring-purple-500 mr-3"
                        />
                        <span className="text-sm font-medium text-gray-300">CIS Registered</span>
                      </label>
                    </div>
                    {newUser.cis_registered && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">UTR Number</label>
                        <input
                          type="text"
                          value={newUser.utr_number}
                          onChange={(e) => setNewUser({...newUser, utr_number: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Bank Information Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    Bank Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Bank Name</label>
                      <input
                        type="text"
                        value={newUser.bank_name}
                        onChange={(e) => setNewUser({...newUser, bank_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Account Number</label>
                      <input
                        type="text"
                        value={newUser.account_number}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                          setNewUser({...newUser, account_number: value});
                        }}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        maxLength="8"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Sort Code</label>
                      <input
                        type="text"
                        value={newUser.sort_code}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                          setNewUser({...newUser, sort_code: value});
                        }}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        maxLength="6"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowAddUserModal(false)}
                    className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

        {/* The add user form will be replaced by a modal */}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th 
                  className="text-left py-4 px-6 font-semibold text-gray-400 text-sm cursor-pointer hover:text-white select-none"
                  onClick={() => handleSort('full_name')}
                >
                  <div className="flex items-center">
                    NAME
                    <SortIcon field="full_name" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="text-left py-4 px-6 font-semibold text-gray-400 text-sm cursor-pointer hover:text-white select-none"
                  onClick={() => handleSort('role')}
                >
                  <div className="flex items-center">
                    ROLE | DEPARTMENT
                    <SortIcon field="role" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="text-left py-4 px-6 font-semibold text-gray-400 text-sm cursor-pointer hover:text-white select-none"
                  onClick={() => handleSort('active')}
                >
                  <div className="flex items-center">
                    STATUS
                    <SortIcon field="active" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th 
                  className="text-left py-4 px-6 font-semibold text-gray-400 text-sm cursor-pointer hover:text-white select-none"
                  onClick={() => handleSort('staff_id')}
                >
                  <div className="flex items-center">
                    STAFF ID
                    <SortIcon field="staff_id" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th className="text-left py-4 px-6 font-semibold text-gray-400 text-sm">ASSIGNED SITES</th>
                <th 
                  className="text-left py-4 px-6 font-semibold text-gray-400 text-sm cursor-pointer hover:text-white select-none"
                  onClick={() => handleSort('net_hourly_rate')}
                >
                  <div className="flex items-center">
                    NET HOURLY RATE
                    <SortIcon field="net_hourly_rate" sortField={sortField} sortDirection={sortDirection} />
                  </div>
                </th>
                <th className="text-right py-4 px-6 font-semibold text-gray-400 text-sm">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {getSortedUsers().map((user, index) => (
                <tr 
                  key={user.id} 
                  className={`${index !== getSortedUsers().length - 1 ? "border-b border-slate-700" : ""} ${user.is_archived ? "opacity-60 bg-slate-800/30" : "hover:bg-slate-700/50 cursor-pointer"} transition-colors duration-200`}
                  onClick={() => !user.is_archived && handleEditUser(user)}
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 bg-gradient-to-br ${user.is_archived ? "from-gray-400 to-gray-600 grayscale" : "from-purple-400 to-purple-600"} rounded-full flex items-center justify-center mr-3 overflow-hidden`}>
                        {user.profile_photo_url ? (
                          <img 
                            src={user.profile_photo_url} 
                            alt={`${user.full_name} profile`} 
                            className={`w-full h-full object-cover ${user.is_archived ? "grayscale" : ""}`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : (
                          <span className="text-white font-medium text-sm">
                            {user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        )}
                        {user.profile_photo_url && (
                          <span className="text-white font-medium text-sm" style={{display: 'none'}}>
                            {user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{user.full_name}</p>
                          {user.is_archived && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400 rounded-full border border-amber-500/30">
                              Archived
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400">{user.username}</p>
                        {(user.phone_number || user.email) && (
                          <div className="text-xs text-gray-500 mt-1">
                            {user.phone_number && <div>📞 {user.phone_number}</div>}
                            {user.email && <div>✉️ {user.email}</div>}
                          </div>
                        )}
                        {user.profile_photo_url && !user.is_archived && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleDeleteProfilePhoto(user.id);
                            }}
                            className="text-xs text-red-400 hover:text-red-300 mt-1"
                            disabled={isUploadingPhoto}
                          >
                            Remove Photo
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div>
                      <p className="font-medium text-white">{user.role}</p>
                      <p className="text-sm text-gray-400">{user.department}</p>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          handleToggleUserStatus(user.id, user.active);
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                          user.active ? 'bg-green-600' : 'bg-slate-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            user.active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className={`ml-3 text-xs font-medium ${
                        user.active ? 'text-green-400' : 'text-gray-500'
                      }`}>
                        {user.active ? 'Active' : 'Inactive'}
                      </span>
                      {/* Password not set indicator */}
                      {user.password_set === false && (
                        <span 
                          className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/20 text-amber-400"
                          title="Password not set - user needs to complete password setup"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          No Password
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-gray-300">{user.staff_id || '-'}</span>
                  </td>
                  <td className="py-4 px-6">
                    {user.assigned_site_names && user.assigned_site_names.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {user.assigned_site_names.map((siteName, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30"
                          >
                            {siteName}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 italic">No sites assigned</span>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm font-medium text-white">
                      {user.net_hourly_rate ? `£${parseFloat(user.net_hourly_rate).toFixed(2)}` : <span className="text-gray-500">Not set</span>}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    {user.is_archived ? (
                      /* Unarchive button for archived users */
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnarchiveUser(user);
                        }}
                        className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium text-green-400 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 transition-colors"
                        data-testid={`unarchive-user-${user.id}`}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Restore
                      </button>
                    ) : (
                      /* Delete button for active users */
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteUser(user);
                        }}
                        disabled={checkingDelete}
                        className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 transition-colors ${checkingDelete ? 'opacity-50 cursor-wait' : ''}`}
                        data-testid={`delete-user-${user.id}`}
                      >
                        {checkingDelete ? (
                          <svg className="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {getSortedUsers().length === 0 && (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-gray-500">
                    {searchTerm ? `No users found matching "${searchTerm}"` : 'No users found. Create a new user to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <div 
            className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Edit User Profile</h3>
                    <p className="text-purple-200">{editingUser.full_name}</p>
                  </div>
                </div>
                <button 
                  onClick={cancelEditUser}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleUpdateUser} className="space-y-8">
                
                {/* Profile Photo Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    Profile Photo
                  </h4>
                  <div className="flex items-center space-x-6">
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center overflow-hidden shadow-lg">
                        {editPhotoPreview ? (
                          <img src={editPhotoPreview} alt="Profile preview" className="w-full h-full object-cover" />
                        ) : editingUser.profile_photo_url ? (
                          <img src={editingUser.profile_photo_url} alt="Current profile" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white font-bold text-lg">
                            {editingUser.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Upload New Photo
                      </label>
                      <input
                        ref={editPhotoInputRef}
                        type="file"
                        onChange={handleEditPhotoSelect}
                        accept="image/*"
                        className="hidden"
                      />
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={() => editPhotoInputRef.current?.click()}
                          disabled={isUploadingPhoto}
                          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          {isUploadingPhoto ? 'Uploading...' : 'Choose File'}
                        </button>
                        {editPhotoPreview && (
                          <button
                            type="button"
                            onClick={clearEditPhoto}
                            className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-gray-400">JPG, PNG up to 5MB</p>
                    </div>
                  </div>
                </div>

                {/* Personal & Login Information Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    Personal & Login Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                      <input
                        type="text"
                        value={editUser.username}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-gray-400 cursor-not-allowed"
                        readOnly
                        title="Username cannot be changed"
                      />
                      <p className="mt-1 text-xs text-gray-400">Username cannot be modified</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editUser.first_name}
                        onChange={(e) => setEditUser({...editUser, first_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editUser.last_name}
                        onChange={(e) => setEditUser({...editUser, last_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Middle Name</label>
                      <input
                        type="text"
                        value={editUser.middle_name}
                        onChange={(e) => setEditUser({...editUser, middle_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={editUser.email}
                        onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                        onBlur={(e) => validateEmail(e.target.value, true, editingUser.id)}
                        className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                          editEmailValidation.isValid === false ? 'border-red-500' : 
                          editEmailValidation.isValid === true ? 'border-green-500' : 'border-slate-600'
                        }`}
                        placeholder="Team member's email"
                        required
                      />
                      {editEmailValidation.message && (
                        <p className={`mt-1 text-xs ${editEmailValidation.isValid ? 'text-green-400' : 'text-red-400'}`}>
                          {editEmailValidation.isValidating ? 'Checking...' : editEmailValidation.message}
                        </p>
                      )}
                      {/* Email Change Warning Banner */}
                      {editingUser && editingUser.email && editUser.email !== editingUser.email && editUser.email.trim() !== '' && (
                        <div className="mt-2 p-3 bg-amber-500/20 border border-amber-500/50 rounded-lg">
                          <div className="flex items-start">
                            <svg className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div className="ml-2">
                              <p className="text-xs font-medium text-amber-300">Email Change Warning</p>
                              <p className="text-xs text-amber-200/80 mt-0.5">
                                Changing this email will require re-verification. The user will not be able to login until they verify their new email address.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Date of Birth</label>
                      <DatePicker
                        selected={editUser.date_of_birth ? new Date(editUser.date_of_birth) : null}
                        onChange={(date) => {
                          if (date) {
                            const formatted = date.toISOString().split('T')[0];
                            setEditUser({...editUser, date_of_birth: formatted});
                          } else {
                            setEditUser({...editUser, date_of_birth: ''});
                          }
                        }}
                        dateFormat="dd/MM/yyyy"
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        yearDropdownItemNumber={100}
                        scrollableYearDropdown
                        maxDate={new Date()}
                        minDate={new Date('1920-01-01')}
                        placeholderText="Select date of birth"
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        wrapperClassName="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={editUser.phone_number}
                        onChange={(e) => setEditUser({...editUser, phone_number: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        placeholder="e.g., +44 7XXX XXXXXX"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                      <input
                        type="text"
                        value={editUser.address}
                        onChange={(e) => setEditUser({...editUser, address: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                    </div>
                  </div>
                  {/* Password Management Section - Conditional based on password_set */}
                  {editingUser.password_set === false ? (
                    <div className="mt-4 p-4 bg-amber-500/20 border border-amber-500/50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="w-5 h-5 text-amber-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h5 className="text-sm font-medium text-amber-300">Password Not Set</h5>
                            <p className="text-sm text-amber-200/80 mt-1">
                              This team member has not yet set their password. You can resend the password setup email if needed.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => handleResendPasswordEmail(
                            editingUser.id, 
                            editingUser.email,
                            editingUser.full_name
                          )}
                          disabled={isResendingPassword || !editingUser.email}
                          className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                        >
                          {isResendingPassword ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Sending...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Resend Password Setup Email
                            </>
                          )}
                        </button>
                        {!editingUser.email && (
                          <p className="text-xs text-amber-400 mt-2">Cannot send email - no email address on file</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h5 className="text-sm font-medium text-blue-300">Password Management</h5>
                          <p className="text-sm text-blue-200/80 mt-1">
                            Team members can reset their password using the &quot;Forgot Password&quot; link in the mobile app or web portal.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Work Details Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6z" />
                      </svg>
                    </div>
                    Work Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={editUser.role}
                        onChange={(e) => setEditUser({...editUser, role: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        required
                      >
                        <option value="">Select Role</option>
                        {availableRoles.map((role) => (
                          <option key={role.id} value={role.role_name}>
                            {role.role_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Department <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={editUser.department}
                        onChange={(e) => setEditUser({...editUser, department: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        required
                      >
                        <option value="">Select Department</option>
                        {availableDepartments.map(dept => (
                          <option key={dept.id} value={dept.department_name}>
                            {dept.department_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Staff ID</label>
                      <input
                        type="text"
                        value={editUser.staff_id}
                        onChange={(e) => setEditUser({...editUser, staff_id: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Assigned Sites</label>
                      <div className="p-3 bg-slate-700 rounded-lg border border-slate-600 min-h-[60px]">
                        <div className="flex flex-wrap gap-2">
                          {sites.length > 0 ? (
                            sites.map(site => {
                              const isAssigned = editUser.assigned_sites.includes(site.id);
                              return (
                                <label
                                  key={site.id}
                                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 ${
                                    isAssigned
                                      ? 'bg-purple-600 text-white shadow-sm'
                                      : 'bg-slate-800 text-gray-300 border border-slate-500 hover:border-purple-400'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isAssigned}
                                    onChange={() => handleEditSiteToggle(site.id, site.site_name, isAssigned)}
                                    className="sr-only"
                                  />
                                  {isAssigned && (
                                    <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                  {site.site_name}
                                </label>
                              );
                            })
                          ) : (
                            <span className="text-sm text-gray-400 italic">No sites available</span>
                          )}
                        </div>
                      </div>
                      <p className="mt-1 text-sm text-gray-400">Click to assign or unassign sites</p>
                    </div>
                  </div>
                </div>

                {/* Financial Information Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    Pay Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Gross Hourly Rate (£) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={editUser.gross_hourly_rate}
                        onChange={(e) => handleEditUserGrossRateChange(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Net Hourly Rate (£)</label>
                      <input
                        type="number"
                        value={editUser.net_hourly_rate}
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-gray-400 cursor-not-allowed focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        step="0.01"
                        min="0"
                        readOnly
                      />
                      <p className="text-xs text-gray-400 mt-1">Automatically calculated as 80% of gross rate if CIS Registered</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">NI Number</label>
                      <input
                        type="text"
                        value={editUser.ni_number}
                        onChange={(e) => setEditUser({...editUser, ni_number: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        placeholder="Optional - Add if available"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editUser.cis_registered}
                          onChange={(e) => handleEditUserCISChange(e.target.checked)}
                          className="rounded border-slate-600 bg-slate-700 text-purple-600 focus:ring-purple-500 mr-3"
                        />
                        <span className="text-sm font-medium text-gray-300">CIS Registered</span>
                      </label>
                    </div>
                    {editUser.cis_registered && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">UTR Number</label>
                        <input
                          type="text"
                          value={editUser.utr_number}
                          onChange={(e) => setEditUser({...editUser, utr_number: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Bank Information Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    Bank Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Bank Name</label>
                      <input
                        type="text"
                        value={editUser.bank_name}
                        onChange={(e) => setEditUser({...editUser, bank_name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Account Number</label>
                      <input
                        type="text"
                        value={editUser.account_number}
                        onChange={(e) => setEditUser({...editUser, account_number: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        maxLength="8"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Sort Code</label>
                      <input
                        type="text"
                        value={editUser.sort_code}
                        onChange={(e) => setEditUser({...editUser, sort_code: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        maxLength="6"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={cancelEditUser}
                    className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                  >
                    Update User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Roles Modal */}
      {showRolesModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden border border-slate-700">
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Manage Roles</h3>
                  <p className="text-purple-200 text-sm">Create and manage team roles</p>
                </div>
              </div>
              <button
                onClick={() => setShowRolesModal(false)}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <RoleManagement 
                roles={availableRoles} 
                fetchRoles={fetchRolesAndDepartments} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Departments Modal */}
      {showDepartmentsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden border border-slate-700">
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Manage Departments</h3>
                  <p className="text-purple-200 text-sm">Create and manage departments</p>
                </div>
              </div>
              <button
                onClick={() => setShowDepartmentsModal(false)}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <DepartmentManagement 
                departments={availableDepartments} 
                fetchDepartments={fetchRolesAndDepartments} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Delete Team Member Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, user: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Team Member?"
        message={`Are you sure you want to delete "${deleteConfirm.user?.first_name} ${deleteConfirm.user?.last_name}"?\n\nThis action cannot be undone and will remove all associated data.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Archive Team Member Confirmation Modal */}
      <ConfirmModal
        isOpen={archiveConfirm.show}
        onClose={() => setArchiveConfirm({ show: false, user: null, clockRecordCount: 0 })}
        onConfirm={handleArchiveConfirm}
        title="Archive Team Member?"
        message={archiveConfirm.clockRecordCount > 0 
          ? `This team member has ${archiveConfirm.clockRecordCount} clock record(s) and cannot be deleted. Archive them instead.\n\nArchiving "${archiveConfirm.user?.first_name} ${archiveConfirm.user?.last_name}" will:\n• Deactivate their account\n• Remove all site assignments\n• Preserve all historical data\n\nYou can restore them later if needed.`
          : `Are you sure you want to archive "${archiveConfirm.user?.first_name} ${archiveConfirm.user?.last_name}"?\n\nThis will:\n• Deactivate their account\n• Remove all site assignments\n• Preserve historical data\n\nYou can restore them later if needed.`
        }
        confirmText="Archive"
        cancelText="Cancel"
        variant="warning"
        isLoading={isArchiving}
      />

      {/* Site Unassignment Warning Modal */}
      <ConfirmModal
        isOpen={siteUnassignWarning.isOpen}
        onClose={cancelSiteUnassignment}
        onConfirm={confirmSiteUnassignment}
        title="Remove Site Assignment"
        message={`Removing ${siteUnassignWarning.userName || 'this user'} from ${siteUnassignWarning.siteName} will end any active clock-in sessions and unassign them from tasks on this site. Do you want to continue?`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="warning"
      />

      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ ...successModal, isOpen: false })}
        title={successModal.title}
        message={successModal.message}
      />

      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
      />

      {/* Email Change Confirmation Modal */}
      {emailChangeModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Confirm Email Change</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-300 mb-4">
                You are changing the email address for <span className="font-semibold">{emailChangeModal.userName}</span>:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center text-sm">
                  <span className="text-gray-500">{emailChangeModal.oldEmail}</span>
                  <svg className="w-4 h-4 mx-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <span className="text-indigo-600 font-medium">{emailChangeModal.newEmail}</span>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 font-medium mb-2">This will:</p>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Require the user to verify their new email address
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Prevent login until verification is complete
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    Send a notification to the old email address
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setEmailChangeModal({ isOpen: false, oldEmail: '', newEmail: '', userName: '', onConfirm: null })}
                className="px-4 py-2 text-white bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={emailChangeModal.onConfirm}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors"
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default UserManagement;